/**
 * What's in the house, grouped by place.
 *
 * Category is shown as an icon only — the icon carries its own accessible name,
 * so nothing is lost by dropping the word. A row is a button: tapping it opens
 * the item for editing, which is also where deleting lives. There is no delete
 * control on the row itself, so a tap can't destroy anything.
 */

import type { CSSProperties, ReactNode } from "react";

import { useAutoAnimate } from "@formkit/auto-animate/react";

import { CATEGORY_RULES } from "../features/food/categories";
import { daysUntilExpiry, effectiveExpiryDate } from "../features/food/expiry";
import { LOCATION_LABELS } from "../features/food/types";
import type { FoodItem, StorageLocation } from "../features/food/types";
import { CATEGORY_ICONS, Icon, LOCATION_ICONS } from "./Icon";

import "./InventoryList.css";

interface InventoryListProps {
  items: FoodItem[];
  /** Which places to list, in order. The parent decides; this just renders. */
  locations: StorageLocation[];
  today: string;
  onOpen: (item: FoodItem) => void;
}

/**
 * How loud the expiry chip should be.
 *
 * Three bands, not a gradient: a chip that shades continuously from green to
 * red is a decoration nobody can read at a glance. "Now" means today or past,
 * "soon" means within three days — the window where you'd change what you cook
 * tonight — and everything else is calm.
 */
function urgency(days: number): "now" | "soon" | "later" {
  if (days <= 0) return "now";
  if (days <= 3) return "soon";
  return "later";
}

/** Plain-language countdown. Reads better on a dashboard than a bare date. */
function expiryLabel(days: number): string {
  if (days < -1) return `${-days} days ago`;
  if (days === -1) return "yesterday";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `${days} days`;
}

/**
 * The rows of one place, animated as they come and go.
 *
 * Its own component only because `useAutoAnimate` returns one ref and there is
 * one list per place — a hook can't be called inside the `.map()` that produces
 * them.
 *
 * What this buys: adding an item slides the ones below it down instead of
 * teleporting them, deleting closes the gap rather than snapping it shut, and
 * an item whose date changed moves to its new position visibly instead of
 * appearing to be a different item. The last one matters most, because the list
 * is sorted by expiry and an edit can move a row a long way.
 *
 * It reads `prefers-reduced-motion` itself and does nothing when it's set, so
 * there is no extra guard here.
 */
function ItemRows({ children }: { children: ReactNode }) {
  const [listRef] = useAutoAnimate<HTMLUListElement>();
  return (
    <ul className="list__items" ref={listRef}>
      {children}
    </ul>
  );
}

export function InventoryList({ items, locations, today, onOpen }: InventoryListProps) {
  return (
    <div className="list">
      {locations.map((location) => {
        const group = items
          .filter((item) => item.location === location)
          .sort((a, b) => effectiveExpiryDate(a).localeCompare(effectiveExpiryDate(b)));

        return (
          <section className="list__group" key={location}>
            <h2 className="list__heading">
              <Icon name={LOCATION_ICONS[location]} className="list__heading-icon" />
              {LOCATION_LABELS[location]}
            </h2>

            {group.length === 0 ? (
              <p className="list__empty">Nothing here yet</p>
            ) : (
              <ItemRows>
                {group.map((item, index) => {
                  const days = daysUntilExpiry(item, today);

                  return (
                    // Capped so a long list doesn't spend a second assembling.
                    <li key={item.id} style={{ "--row": Math.min(index, 8) } as CSSProperties}>
                      <button
                        className="list__item"
                        type="button"
                        onClick={() => onOpen(item)}
                      >
                        <span
                          className="list__item-media"
                          data-category={item.category}
                        >
                          {item.photo ? (
                            <>
                              {/* The name is right beside it, so the picture is
                                  decorative — but the category was being announced
                                  by the icon it replaces, so say it here instead. */}
                              <img className="list__item-photo" src={item.photo} alt="" />
                              <span className="sr-only">
                                {CATEGORY_RULES[item.category].label}
                              </span>
                            </>
                          ) : (
                            <Icon
                              name={CATEGORY_ICONS[item.category]}
                              className="list__item-icon"
                              label={CATEGORY_RULES[item.category].label}
                            />
                          )}
                        </span>

                        <span className="list__item-name">{item.name}</span>

                        <span className="list__item-amount">
                          {item.quantity} {item.unit}
                        </span>

                        <span
                          className="list__item-expiry"
                          data-expired={days < 0 || undefined}
                          data-urgency={urgency(days)}
                        >
                          {/* Past its date, the dot carries the warning so the
                              words can stay monochrome like everything else. */}
                          {days < 0 && <Icon name="dot" className="list__item-dot" />}
                          {expiryLabel(days)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ItemRows>
            )}
          </section>
        );
      })}
    </div>
  );
}
